import type { ServerSideSelectionState } from '@civilgrid/ui/components';
import { useMemo } from 'react';
import type { ProjectsSearchPayload } from '~/api/projectsV3';
import authorizations, { Action } from '~/authorizations';
import type { Project } from '~/common/types';
// import { useBulkBookmark } from './useBulkBookmark';
import { useBulkEditDates } from './useBulkEditDates';
import { useBulkInvite } from './useBulkInvite';
import { useBulkReassign } from './useBulkReassign';

export type BulkDialogType = 'projectManager' | 'editDates' | 'invite' | null;

export type ProjectDates = {
  startDate?: string;
  endDate?: string;
  constructionStartDate?: string;
  constructionEndDate?: string;
  projectManagerId?: string;
};

export type { ProjectManagerOption } from './useBulkReassign';

export type UseBulkActionsParams = {
  organizationId: string | null;
  buildSearchPayloadForBulk: () => ProjectsSearchPayload;
  orgMembers: Array<{
    user?: {
      id: string;
      status?: string;
      firstName?: string;
      lastName?: string;
      name?: string;
      email?: string;
    };
  }>;
  /** Resolves project by id (e.g. from table data). Used for pre-filling bulk dialogs and single-project invite members list. */
  getProjectById?: (id: string) => Project | undefined;
};

export function useBulkActions({
  organizationId,
  buildSearchPayloadForBulk,
  orgMembers,
  getProjectById,
}: UseBulkActionsParams) {
  const reassign = useBulkReassign({
    organizationId,
    buildSearchPayloadForBulk,
    orgMembers,
    getProjectById,
  });

  const editDates = useBulkEditDates({
    organizationId,
    buildSearchPayloadForBulk,
    getProjectById,
  });

  const invite = useBulkInvite({
    organizationId,
    buildSearchPayloadForBulk,
    getProjectById,
  });

  // const bookmark = useBulkBookmark({
  //   organizationId,
  //   buildSearchPayloadForBulk,
  // });

  const canEditProjectDetails = authorizations.can(Action.EditProjectDetails);
  const canInvite = authorizations.can(Action.InviteOrgMembers) && authorizations.can(Action.AddProjectMember);

  const bulkActions = useMemo(
    () =>
      [
        canEditProjectDetails && {
          label: 'Reassign',
          icon: 'shuffle' as const,
          onClick: (selection: ServerSideSelectionState) => reassign.open(selection),
        },
        canEditProjectDetails && {
          label: 'Edit Dates',
          icon: 'calendar' as const,
          onClick: (selection: ServerSideSelectionState) => editDates.open(selection),
        },
        canInvite && {
          label: 'Invite Team',
          icon: 'luUserPlus' as const,
          onClick: (selection: ServerSideSelectionState) => invite.open(selection),
        },
        // {
        //   label: 'Bookmark',
        //   icon: 'bookmark' as const,
        //   onClick: bookmark.handleBulkBookmark,
        // },
      ].filter(Boolean) as Array<{
        label: string;
        icon: 'shuffle' | 'calendar' | 'luUserPlus';
        onClick: (selection: ServerSideSelectionState) => void;
      }>,
    [reassign.open, editDates.open, invite.open, canEditProjectDetails, canInvite],
  );

  return {
    bulkActions,
    reassign: {
      open: reassign.isOpen,
      close: reassign.close,
      projectManagerId: reassign.projectManagerId,
      setProjectManagerId: reassign.setProjectManagerId,
      projectManagerOptions: reassign.projectManagerOptions,
      onSubmit: reassign.onSubmit,
      isPending: reassign.isPending,
    },
    editDates: {
      open: editDates.isOpen,
      close: editDates.close,
      startDate: editDates.startDate,
      setStartDate: editDates.setStartDate,
      endDate: editDates.endDate,
      setEndDate: editDates.setEndDate,
      constructionStartDate: editDates.constructionStartDate,
      setConstructionStartDate: editDates.setConstructionStartDate,
      constructionEndDate: editDates.constructionEndDate,
      setConstructionEndDate: editDates.setConstructionEndDate,
      onSubmit: editDates.onSubmit,
      isPending: editDates.isPending,
    },
    invite: {
      open: invite.isOpen,
      close: invite.close,
      inviteDialogProps: invite.inviteDialogProps,
      isPending: invite.isPending,
    },
  };
}
